import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Camera, CheckSquare, Ruler, CalendarDays, Share2, X } from "lucide-react";
import { toast } from "sonner";
import { trackEvent } from "../lib/analytics";

const ACTIONS = [
  { to: "/prescriptions/new",       icon: Camera,        label: "Scan label",       hint: "Snap a prescription",   testid: "qa-scan" },
  { to: "/todo",                    icon: CheckSquare,   label: "Add to-do",        hint: "Quick capture",         testid: "qa-todo" },
  { to: "/tools/unit-converter",    icon: Ruler,         label: "Measure",          hint: "Unit converter",        testid: "qa-measure" },
  { to: "/tools/calendar-generator",icon: CalendarDays,  label: "Calendar",         hint: "Printable month PDF",   testid: "qa-calendar" },
];

export default function QuickActionSheet({ open, onClose }) {
  useEffect(() => {
    if (!open) return;
    const esc = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", esc);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", esc);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  async function shareApp() {
    const url = window.location.origin + "/";
    const title = "VeriHandi — very handy tools for everyday tasks";
    const text = "A quiet set of tools for the small, stubborn tasks of daily life.";
    try {
      if (navigator.share) {
        await navigator.share({ title, text, url });
        trackEvent("share_click", { method: "web_share_api", source: "quick_actions" });
        onClose();
        return;
      }
    } catch { return; }
    try {
      await navigator.clipboard.writeText(url);
      trackEvent("share_click", { method: "clipboard", source: "quick_actions" });
      toast.success("Link copied — paste it anywhere");
      onClose();
    } catch {
      toast.error("Could not copy link");
    }
  }

  if (!open) return null;

  return (
    <div className="md:hidden fixed inset-0 z-[60] flex items-end" data-testid="quick-action-sheet">
      <div
        className="absolute inset-0 bg-black/50 animate-[fade-up_200ms_ease]"
        onClick={onClose}
      />
      <div
        className="relative w-full rounded-t-3xl p-5 pb-[max(24px,env(safe-area-inset-bottom))] animate-[fade-up_280ms_cubic-bezier(.2,.8,.2,1)]"
        style={{
          background: "var(--surface)",
          borderTop: "1px solid var(--border-subtle)",
          boxShadow: "0 -20px 40px -10px rgba(0,0,0,0.25)",
        }}
      >
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="label-caps">Quick actions</div>
            <h3 className="font-display font-bold text-xl tracking-tight mt-0.5">What would you like to do?</h3>
          </div>
          <button onClick={onClose} data-testid="qa-close" className="h-9 w-9 rounded-lg hover:bg-[color:var(--surface-2)] flex items-center justify-center">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="grid grid-cols-2 gap-3">
          {ACTIONS.map(({ to, icon: Icon, label, hint, testid }) => (
            <Link
              key={to}
              to={to}
              onClick={onClose}
              data-testid={testid}
              className="card-tactile rounded-2xl p-4 flex flex-col items-start gap-2 no-underline text-[color:var(--text-main)]"
            >
              <div className="h-10 w-10 rounded-xl bg-[color:var(--primary-soft)] flex items-center justify-center">
                <Icon className="h-5 w-5 text-[color:var(--primary-ink)]" />
              </div>
              <div>
                <div className="font-display font-bold text-sm">{label}</div>
                <div className="text-xs mt-0.5" style={{ color: "var(--text-muted)" }}>{hint}</div>
              </div>
            </Link>
          ))}
        </div>

        <button
          onClick={shareApp}
          data-testid="qa-share"
          className="mt-4 w-full flex items-center justify-center gap-2 h-12 rounded-xl font-semibold text-sm transition-colors"
          style={{ background: "var(--accent)", color: "white" }}
        >
          <Share2 className="h-4 w-4" /> Share VeriHandi
        </button>
      </div>
    </div>
  );
}
